import { useTranslation } from "react-i18next";
import { z } from "zod";
import { regexIsZipCode, regexNonEmptyString } from "../../common/regex";

export type CompanyRegistryPayload = {
	name: string;
	address: string;
	zipCode: string;
	city: string;
	province: string;
	country: string;
	vat: string;
	fiscalPeriod: string;
	tripsLimit?: number;
	nodesLimit?: number;
	nodesDetailsLimit?: number;
	connectionType?: string;
	connectionDir?: string;
	connectionSubDir?: string;
	dirStructure?: string;
};

export const emptyCompanyRegistryPayload: CompanyRegistryPayload = {
	name: "",
	address: "",
	zipCode: "",
	city: "",
	province: "",
	country: "",
	vat: "",
	fiscalPeriod: "",
};

export const useCompaniesValidationSchemas = () => {
	const { t } = useTranslation("validation");

	/* Possible VAT numbers
    
    (AT)?U[0-9]{8} |                              # Austria
    (BE)?0[0-9]{9} |                              # Belgium
    (BG)?[0-9]{9,10} |                            # Bulgaria
    (CY)?[0-9]{8}L |                              # Cyprus
    (CZ)?[0-9]{8,10} |                            # Czech Republic
    (DE)?[0-9]{9} |                               # Germany
    (DK)?[0-9]{8} |                               # Denmark
    (EE)?[0-9]{9} |                               # Estonia
    (EL|GR)?[0-9]{9} |                            # Greece
    (ES)?[0-9A-Z][0-9]{7}[0-9A-Z] |               # Spain
    (FI)?[0-9]{8} |                               # Finland
    (FR)?[0-9A-Z]{2}[0-9]{9} |                    # France
    (GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3}) | # United Kingdom
    (HU)?[0-9]{8} |                               # Hungary
    (IE)?[0-9]S[0-9]{5}L |                        # Ireland
    (IT)?[0-9]{11} |                              # Italy
    (LT)?([0-9]{9}|[0-9]{12}) |                   # Lithuania
    (LU)?[0-9]{8} |                               # Luxembourg
    (LV)?[0-9]{11} |                              # Latvia
    (MT)?[0-9]{8} |                               # Malta
    (NL)?[0-9]{9}B[0-9]{2} |                      # Netherlands
    (PL)?[0-9]{10} |                              # Poland
    (PT)?[0-9]{9} |                               # Portugal
    (RO)?[0-9]{2,10} |                            # Romania
    (SE)?[0-9]{12} |                              # Sweden
    (SI)?[0-9]{8} |                               # Slovenia
    (SK)?[0-9]{10}                                # Slovakia

    */

	const UpdateCompanySchema = z.object({
		name: z.string().regex(regexNonEmptyString, t("common/required")),

		address: z.string().regex(regexNonEmptyString, t("common/required")),

		// A valid zip code could be either
		// 12345 or 12345-1234
		zipCode: z
			.string()
			.regex(regexNonEmptyString, t("common/required"))
			.regex(regexIsZipCode, t("common/invalid zip code")),

		city: z.string().regex(regexNonEmptyString, t("common/required")),

		province: z.string().regex(regexNonEmptyString, t("common/required")).min(2, t("common/invalid province")),

		// country: z.string()
		//         .regex(regexNonEmptyString, t("common/required")),
		country: z.string(),

		vat: z
			.string()
			.regex(regexNonEmptyString, t("common/required"))
			.min(7, t("common/invalid vat num"))
			.max(12, t("common/invalid vat num")),

		fiscalPeriod: z.string(),
		tripsLimit: z.number().optional(),
		nodesLimit: z.number().optional(),
		nodesDetailsLimit: z.number().optional(),
		connectionType: z.string().optional(),
		connectionDir: z.string().optional(),
		connectionSubDir: z.string().optional(),
		dirStructure: z.string().optional(),
	});

	return { UpdateCompanySchema };
};
